import { Loading } from '@workwhile/ui'
import React, { PropsWithChildren, useCallback, useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { WhoamiResponse } from 'api/admin/userPermissions/getWhoami'
import { useWhoamiQuery } from 'queries/admin/userPermissions'

type WhoamiPermissionName = WhoamiResponse['permissions'][number]['permission']

type WhoamiContextState = {
  permissions: WhoamiResponse['permissions']
  hasPermission: (permission: WhoamiPermissionName) => boolean
  companyScopes: WhoamiResponse['companyScopes']
  hasCompanyScope: (companyId: number, companyGroupId: number | null) => boolean
}

const [Context, useWhoamiContextValue] = createContext<WhoamiContextState>({
  name: 'WhoamiContext',
})

export const WhoamiProvider = ({ children }: PropsWithChildren) => {
  const { data = { permissions: [], companyScopes: [] }, isPending } =
    useWhoamiQuery()

  const permissions = data.permissions
  const companyScopes = data.companyScopes

  const hasAllCompanies = useMemo(
    () => companyScopes.some((companyScope) => companyScope.allCompanies),
    [companyScopes]
  )

  const allCompanyIDs = useMemo(
    () =>
      new Set(
        companyScopes
          .map((companyScope) => companyScope.companyId)
          .filter((companyId) => companyId !== null)
      ),
    [companyScopes]
  )

  const allCompanyGroupIDs = useMemo(
    () =>
      new Set(
        companyScopes
          .map((companyScope) => companyScope.companyGroupId)
          .filter((companyGroupId) => companyGroupId !== null)
      ),
    [companyScopes]
  )

  const hasPermission = useCallback(
    (permission: WhoamiPermissionName) =>
      permissions.some((item) => item.permission === permission),
    [permissions]
  )

  const hasCompanyScope = useCallback(
    (companyId: number, companyGroupId: number | null) => {
      if (hasAllCompanies) {
        return true
      }

      if (companyGroupId !== null && allCompanyGroupIDs.has(companyGroupId)) {
        return true
      }

      if (allCompanyIDs.has(companyId)) {
        return true
      }

      return false
    },
    [hasAllCompanies, allCompanyIDs, allCompanyGroupIDs]
  )

  const value: WhoamiContextState = useMemo(
    () => ({
      permissions,
      hasPermission,
      companyScopes,
      hasCompanyScope,
    }),
    [permissions, hasPermission, companyScopes, hasCompanyScope]
  )

  if (isPending) {
    return <Loading />
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useWhoamiContextValue }
